import { useTheme } from '@material-ui/core';
import { useMemo } from 'react';
import hexToRGBA from '~utils/hex-to-rgba';

export const useSelectStyles = (hasError) => {
  const theme = useTheme();

  return useMemo(
    () => ({
      control: (provided, state) => {
        const getControlStyles = () => {
          const { isFocused, isDisabled } = state;

          if (isDisabled) {
            const borderColor = theme.colors.greyE8;
            const backgroundColor = theme.colors.greyF7;
            return {
              '&:hover': { borderColor, backgroundColor },
              borderColor,
              backgroundColor,
              boxShadow: null,
            };
          }

          if (hasError) {
            const borderColor = theme.colors.red;
            return {
              '&:hover': { borderColor, backgroundColor: hexToRGBA(theme.colors.redBg, 0.32) },
              borderColor,
              backgroundColor: provided.backgroundColor,
              boxShadow: isFocused ? `0 0 4px 0 ${hexToRGBA(theme.colors.red, 0.64)}` : null,
            };
          }

          const borderColor = state.isFocused ? theme.colors.brandAction : theme.colors.greyCc;
          return {
            '&:hover': { borderColor, backgroundColor: theme.colors.greyFa },
            borderColor,
            backgroundColor: provided.backgroundColor,
            boxShadow: isFocused ? `0 0 4px 0 ${hexToRGBA(theme.colors.brandAction, 0.64)}` : null,
          };
        };

        const { '&:hover': hover, borderColor, backgroundColor, boxShadow } = getControlStyles();
        return { ...provided, '&:hover': hover, borderColor, backgroundColor, boxShadow };
      },

      dropdownIndicator: (provided, state) => ({
        ...provided,
        ':hover': { color: theme.colors.grey66 },
        color: state.isDisabled ? theme.colors.grey99 : theme.colors.grey66,
        padding: 10,
        fontSize: 10,
        transition: 'transform 150ms cubic-bezier(0.65, 0.05, 0.36, 1)',
        transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : null,
      }),

      input: (provided) => ({
        ...provided,
        ...theme.typography.body1,
        marginBottom: 0,
        marginTop: 0,
        paddingBottom: 0,
        paddingTop: 0,
        color: theme.colors.dark,
      }),

      menu: (provided) => ({
        ...provided,
        boxShadow: `0 2px 8px 0 ${hexToRGBA(theme.colors.dark, 0.16)}`,
        marginBottom: 2,
        marginTop: 2,
      }),

      noOptionsMessage: (provided) => ({
        ...provided,
        ...theme.typography.body1,
        padding: '2px 12px 6px',
        color: theme.colors.grey99,
        textAlign: 'start',
      }),

      option: (provided, state) => ({
        ...provided,
        ...theme.flex.crossCenter,
        ':active': { backgroundColor: 'inherit' },
        ...theme.typography.dropdown,
        ...(state.isMulti ? { display: 'flex', alignItems: 'start' } : null),
        padding: '6px 12px',
        color:
          state.isSelected && !state?.isDisabled
            ? theme.colors.brandAction
            : state?.isDisabled
            ? theme.colors.grey99
            : theme.colors.dark,
        backgroundColor: state.isFocused ? theme.colors.brandBg : provided.background,
        cursor: 'pointer',
      }),

      placeholder: (provided) => ({
        ...provided,
        ...theme.typography.body1,
        color: theme.colors.grey99,
      }),

      singleValue: (provided, state) => ({
        ...provided,
        ...theme.typography.body1,
        color: state.isDisabled ? theme.colors.grey99 : theme.colors.dark,
      }),

      valueContainer: (provided) => ({
        ...provided,
        padding: 0,
        margin: '7px -2px 7px 12px',
        minHeight: 24,
      }),
    }),
    [
      hasError,
      theme.colors.brandAction,
      theme.colors.brandBg,
      theme.colors.dark,
      theme.colors.grey66,
      theme.colors.grey99,
      theme.colors.greyCc,
      theme.colors.greyE8,
      theme.colors.greyF7,
      theme.colors.greyFa,
      theme.colors.red,
      theme.colors.redBg,
      theme.typography.body1,
      theme.typography.dropdown,
      theme.flex.crossCenter,
    ]
  );
};
